import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { CreditCard, LogIn, HelpCircle, Info, Menu, X, LayoutDashboard, User, Shield, Plus } from "lucide-react";
import { useState } from "react";

/**
 * GlobalNav - Marketing pages navigation
 * Used on: /, /pricing, /faq, /about, /sample-report, /terms
 * 
 * Links: Logo, Pricing, FAQ, About
 * Right: Login/Register (if not logged) or Dashboard/Admin/Account (if logged)
 */
export default function GlobalNav() {
  const { user, isAdmin } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const isActive = (path) => location.pathname === path;

  const navLinks = [
    { path: '/pricing', label: 'Pricing', icon: CreditCard },
    { path: '/faq', label: 'FAQ', icon: HelpCircle },
    { path: '/about', label: 'About', icon: Info },
  ];

  return (
    <>
      {/* Desktop Navigation */}
      <nav className="hidden md:flex fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-white/5">
        <div className="w-full max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          {/* Left: Logo + Links */}
          <div className="flex items-center gap-6">
            <Link to="/" className="flex items-center">
              <img src="/scorecite-logo.png" alt="ScoreCite" className="h-8" />
            </Link>
            <div className="flex items-center gap-1">
              {navLinks.map(({ path, label, icon: Icon }) => (
                <Link key={path} to={path}>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className={isActive(path) 
                      ? 'text-white bg-white/10' 
                      : 'text-zinc-400 hover:text-white'
                    }
                  >
                    <Icon className="w-4 h-4 mr-1" /> {label}
                  </Button>
                </Link>
              ))}
            </div>
          </div>
          
          {/* Right: Auth */}
          <div className="flex items-center gap-3">
            {user ? (
              <>
                <Link to="/dashboard">
                  <Button variant="ghost" size="sm" className="text-blue-400 hover:text-blue-300">
                    <LayoutDashboard className="w-4 h-4 mr-1" /> Dashboard
                  </Button>
                </Link>
                {isAdmin && (
                  <Link to="/admin">
                    <Button variant="ghost" size="sm" className="text-purple-400 hover:text-purple-300">
                      <Shield className="w-4 h-4 mr-1" /> Admin
                    </Button>
                  </Link>
                )}
                <span className="text-zinc-600">|</span>
                <span className="text-zinc-500 text-sm flex items-center gap-1">
                  <User className="w-3 h-3" /> {user.email}
                </span>
              </>
            ) : (
              <Link to="/login">
                <Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white">
                  <LogIn className="w-4 h-4 mr-1" /> Login / Register
                </Button>
              </Link>
            )}
          </div>
        </div>
      </nav>

      {/* Mobile: Top bar background */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 h-14 bg-[#0A0A0A] border-b border-white/10" />

      {/* Mobile: Logo */}
      <div className="md:hidden fixed top-3 left-4 z-50">
        <Link to="/">
          <img src="/scorecite-logo.png" alt="ScoreCite" className="h-8" />
        </Link>
      </div>

      {/* Mobile: Hamburger */}
      <button
        className="md:hidden fixed top-3 right-4 z-50 p-2 bg-[#121212] border border-white/10 rounded-lg"
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
      >
        {mobileMenuOpen ? <X className="w-6 h-6 text-white" /> : <Menu className="w-6 h-6 text-white" />}
      </button>

      {/* Mobile: Menu overlay */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-[#0A0A0A] pt-20">
          <div className="flex flex-col items-center gap-2 p-6">
            {navLinks.map(({ path, label, icon: Icon }) => (
              <Link key={path} to={path} onClick={() => setMobileMenuOpen(false)} className="w-full">
                <Button variant="ghost" className={`w-full justify-start text-lg ${isActive(path) ? 'text-white bg-white/10' : 'text-zinc-300'}`}>
                  <Icon className="w-5 h-5 mr-3" /> {label}
                </Button>
              </Link>
            ))}
            
            <div className="w-full border-t border-white/10 pt-4 mt-2">
              {user ? (
                <>
                  <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)} className="w-full block mb-2">
                    <Button variant="ghost" className="w-full justify-start text-blue-400 text-lg">
                      <LayoutDashboard className="w-5 h-5 mr-3" /> Dashboard
                    </Button>
                  </Link>
                  <Link to="/full-audit" onClick={() => setMobileMenuOpen(false)} className="w-full block mb-2">
                    <Button variant="ghost" className="w-full justify-start text-zinc-300 text-lg">
                      <Plus className="w-5 h-5 mr-3" /> New Audit
                    </Button>
                  </Link>
                  {isAdmin && (
                    <Link to="/admin" onClick={() => setMobileMenuOpen(false)} className="w-full block mb-2">
                      <Button variant="ghost" className="w-full justify-start text-purple-400 text-lg">
                        <Shield className="w-5 h-5 mr-3" /> Admin Panel
                      </Button>
                    </Link>
                  )}
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <p className="text-zinc-500 text-sm text-center">{user.email}</p>
                  </div>
                </>
              ) : (
                <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="w-full block">
                  <Button className="w-full bg-blue-600 hover:bg-blue-500 text-white text-lg py-6">
                    <LogIn className="w-5 h-5 mr-3" /> Login / Register
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Spacer */}
      <div className="h-14" />
    </>
  );
}
